import { useEffect, useRef, useState } from 'react';
import type { DesignConfig, LifestyleReason, LifestyleState } from '../api/types';
import {
  getLifestyle,
  isCustomElement,
  isLifestyleActive,
  productSplit,
  resolveProductImageBox,
  splitWidths,
} from '../api/types';
import { CANVAS_SIZE, toDisplay } from '../lib/canvas-coords';
import { ELEMENT_BOXES } from '../lib/elementBoxes';
import DraggableElement from './DraggableElement';

interface Props {
  config: DesignConfig;
  previewUrl: string | null;
  isLoading: boolean;
  /** What the server DID with the lifestyle photo on the last render. The
   *  config alone can't tell us: an unresolved photo renders a full-canvas ad
   *  that looks identical to the mode being off, and the drag layer has to
   *  follow the render or every handle lands in the wrong place. */
  lifestyleState: LifestyleState;
  lifestyleReason: LifestyleReason | null;
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  onPositionChange: (key: string, x: number, y: number) => void;
  onProductBoxChange: (box: { x: number; y: number; w: number; h: number }) => void;
}

/** Why no photo made it into the render, in the operator's terms. */
const UNRESOLVED_COPY: Record<LifestyleReason, string> = {
  no_field_value: 'This product has no value in that field',
  no_asset: 'No image uploaded yet',
  fetch_failed: "The image couldn't be loaded",
};

export default function Canvas({
  config,
  previewUrl,
  isLoading,
  lifestyleState,
  lifestyleReason,
  selectedKey,
  onSelect,
  onPositionChange,
  onProductBoxChange,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(800);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      setSize(Math.min(w, 800));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Lifestyle mode confines the draggable layer to the product half. Element
  // x/y are half-space absolutes, and the half is pasted into the 1080 canvas
  // 1:1, so the display scale is unchanged — only the origin and the clamp
  // width move. With lifestyle off these are 0 / 1080 and every element behaves
  // exactly as before.
  //
  // The geometry keys off the RENDER state (`productSplit`), NOT off the
  // config: when the photo doesn't resolve the server composes at the full
  // 1080, so confining the handles to a half the render never used would put
  // every one of them in the wrong place. The Editor reads the same helper for
  // the element panel, so the two can never resolve the box at different widths.
  const lifestyle = getLifestyle(config.elements);
  const splitActive = isLifestyleActive(config.elements);
  const splitRendered = splitActive && lifestyleState === 'resolved';
  const splitMissing = splitActive && lifestyleState === 'unresolved';
  const [productW, lifestyleW] = productSplit(config.elements, lifestyleState);
  // Where the photo WOULD go — used to place the "not rendering" marker even
  // though the drag layer spans the full canvas in that state.
  const [plannedProductW, plannedLifestyleW] = splitWidths(
    CANVAS_SIZE, lifestyle?.ratio ?? 0.5,
  );
  const photoOnLeft = splitActive && lifestyle?.side !== 'right';
  const productOriginX = photoOnLeft ? lifestyleW : 0;

  // Only the bare surface deselects — a handle's own mousedown selects it and
  // must not be undone by the bubbling event, so this only acts when the
  // event fires on the element the listener is attached to, not a descendant.
  function onSurfaceMouseDown(e: React.MouseEvent) {
    if (e.target === e.currentTarget) onSelect(null);
  }

  return (
    <div ref={containerRef} className="w-full max-w-[800px] mx-auto">
      <div
        className="relative aspect-square overflow-hidden rounded-2xl border border-ff-line bg-white shadow-soft"
        style={{ width: size, height: size }}
        onMouseDown={onSurfaceMouseDown}
      >
        {/* Preview image */}
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="rendered preview"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              isLoading ? 'opacity-50' : 'opacity-100'
            }`}
            onMouseDown={() => onSelect(null)}
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center text-ff-mute">
              <div className="font-display text-3xl text-ff-forest mb-2">{config.name}</div>
              <p className="font-mono text-[11px] uppercase tracking-widish">Awaiting render</p>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute top-3 right-3 z-30 ff-chip bg-ff-forest/90 text-ff-ivory animate-pulse">
            <span className="h-1.5 w-1.5 rounded-full bg-ff-ivory" />
            Rendering
          </div>
        )}

        {/* Lifestyle half — the photo itself is already IN the server-rendered
            preview, so this only marks the seam. It must not cover the preview. */}
        {splitRendered && (
          <div
            data-testid="lifestyle-half"
            className="absolute top-0 bottom-0 z-10 pointer-events-none border-ff-forest/25"
            style={{
              left: photoOnLeft ? 0 : toDisplay(productW, size),
              width: toDisplay(lifestyleW, size),
              [photoOnLeft ? 'borderRightWidth' : 'borderLeftWidth']: 2,
              borderStyle: 'dashed',
            }}
          >
            {!previewUrl && (
              <div className="absolute inset-0 grid place-items-center bg-ff-sand/40">
                <span className="font-mono text-[10px] uppercase tracking-widish text-ff-mute">
                  Lifestyle photo
                </span>
              </div>
            )}
            <span className="absolute left-1.5 top-1.5 ff-chip bg-ff-forest/80 text-ff-ivory text-[9px]">
              lifestyle {Math.round((lifestyle?.ratio ?? 0.5) * 100)}%
            </span>
          </div>
        )}

        {/* Lifestyle ON but no photo resolved. The preview underneath is a
            FULL-CANVAS ad, so this marks where the half would have gone and
            says so outright. Round 1 drew the seam here regardless, which read
            as "the split is on" over an ad that had no split in it. */}
        {splitMissing && (
          <div
            data-testid="lifestyle-missing"
            className="absolute top-0 bottom-0 z-10 pointer-events-none
                       border-2 border-dashed border-ff-warning/45 bg-ff-warning-soft/70
                       backdrop-blur-[1px] grid place-items-center px-3 text-center"
            style={{
              left: photoOnLeft ? 0 : toDisplay(plannedProductW, size),
              width: toDisplay(plannedLifestyleW, size),
            }}
          >
            <div className="space-y-1.5">
              <span className="ff-chip bg-ff-warning-soft text-ff-warning text-[9px]">
                No photo
              </span>
              <p className="text-[11px] leading-snug text-ff-forest">
                {lifestyleReason ? UNRESOLVED_COPY[lifestyleReason] : 'No image resolved'}
              </p>
              <p className="font-mono text-[9px] uppercase tracking-widish text-ff-mute">
                rendering full canvas
              </p>
            </div>
          </div>
        )}

        {/* Draggable layer. In lifestyle mode this wrapper IS the product half,
            so Rnd's bounds="parent" clamps every handle to it and each x/y is
            read straight as a half-space absolute — no per-element offset. */}
        <div
          data-testid="drag-layer"
          className="absolute top-0 bottom-0 z-20"
          style={{
            left: toDisplay(productOriginX, size),
            width: toDisplay(productW, size),
          }}
          onMouseDown={onSurfaceMouseDown}
        >
          {/* Product-image fit box — rendered first so it sits behind the
              badge handles. The cutout is fitted and centred inside this box,
              which is why the handle is box-sized rather than product-sized. */}
          {(() => {
            const box = resolveProductImageBox(config.elements.product_image, productW);
            return (
              <DraggableElement
                label="product_image"
                x={box.x}
                y={box.y}
                width={box.w}
                height={box.h}
                displaySize={size}
                boundsWidth={productW}
                color="var(--ff-orange)"
                selected={selectedKey === 'product_image'}
                resizable
                onSelect={() => onSelect('product_image')}
                onChange={(x, y) => onPositionChange('product_image', x, y)}
                onResize={(x, y, w, h) => onProductBoxChange({ x, y, w, h })}
              />
            );
          })()}

          {/* Draggable overlays */}
          {(Object.keys(ELEMENT_BOXES) as Array<keyof typeof ELEMENT_BOXES>).map((key) => {
            const el = (config.elements as unknown as Record<string, { x: number; y: number; visible: boolean }>)[key];
            if (!el || el.visible === false) return null;
            const box = ELEMENT_BOXES[key];
            return (
              <DraggableElement
                key={key}
                label={key}
                x={el.x}
                y={el.y}
                width={box.w}
                height={box.h}
                displaySize={size}
                boundsWidth={productW}
                color={box.color}
                selected={selectedKey === key}
                onSelect={() => onSelect(key)}
                onChange={(x, y) => onPositionChange(key, x, y)}
              />
            );
          })}

          {/* Custom overlays (tier 3) */}
          {Object.entries(config.elements).map(([key, el]) => {
            if (!isCustomElement(el)) return null;
            if (el.visible === false) return null;
            const labelText = el.type === 'text' ? `"${el.value}"` : `:${el.field}`;
            // Approximate width from font_size + text length; height = fontSize * 1.7
            const charW = (el.font_size ?? 28) * 0.6;
            const labelLen = (el.type === 'text' ? el.value : `:${el.field}`).length;
            const w = Math.max(120, charW * Math.max(4, labelLen));
            const h = Math.max(60, Math.round((el.font_size ?? 28) * 1.7));
            return (
              <DraggableElement
                key={key}
                label={`${key} ${labelText}`}
                x={el.x}
                y={el.y}
                width={w}
                height={h}
                displaySize={size}
                boundsWidth={productW}
                color="var(--ff-blue)"
                selected={selectedKey === key}
                onSelect={() => onSelect(key)}
                onChange={(x, y) => onPositionChange(key, x, y)}
              />
            );
          })}
        </div>

        {/* Subtle grid */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(to right, var(--ff-ink) 1px, transparent 1px), linear-gradient(to bottom, var(--ff-ink) 1px, transparent 1px)',
            backgroundSize: `${size / 12}px ${size / 12}px`,
          }}
        />
      </div>

      <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widish text-ff-mute">
        {splitRendered
          ? `1080 × 1080 · ${lifestyleW}px photo ${photoOnLeft ? 'left' : 'right'} / ${productW}px product · drag within the product half`
          : splitMissing
            ? '1080 × 1080 · no photo for this product · rendering full canvas'
            : '1080 × 1080 · drag elements · server-rendered preview refreshes as you edit'}
      </p>
    </div>
  );
}
